import { StockWithVariant, getTodayDailyStock, recomputeAllStock } from "@/api/daily-stock-api";
import { useMutation } from "@/hooks/use-api";
import { PageContent } from "@/types/api-response";
import useSWRInfinite from "swr/infinite";

const DAILY_STOCK_ENDPOINT = "/daily-stocks";
const PAGE_SIZE = 20;

export function useTodayDailyStock(params?: { search?: string; status?: string }) {
  const { data: pages, error, isLoading, isValidating, mutate, size, setSize } =
    useSWRInfinite<{ data: PageContent<StockWithVariant>; success: boolean }>(
      (pageIndex) => [DAILY_STOCK_ENDPOINT, pageIndex, params?.search, params?.status],
      ([, pageIndex]) =>
        getTodayDailyStock({
          page: pageIndex as number,
          size: PAGE_SIZE,
          search: params?.search,
          status: params?.status,
        }),
      { refreshInterval: 30000, revalidateOnFocus: false, revalidateFirstPage: false }
    );

  const stocks: StockWithVariant[] = Array.isArray(pages)
    ? pages.flatMap((p) => (Array.isArray(p?.data?.content) ? p.data.content : []))
    : [];
  const lastPage = pages?.[pages.length - 1];

  return {
    data: stocks,
    totalElements: lastPage?.data?.totalElements ?? 0,
    isLastPage: lastPage?.data?.last ?? true,
    isLoading,
    isValidating,
    error,
    loadMore: () => { if (!lastPage?.data?.last && !isValidating) setSize(size + 1); },
    refresh: () => mutate(),
  };
}

export function useRecomputeAllStock() {
  return useMutation(() => recomputeAllStock(), {
    invalidateKeys: [DAILY_STOCK_ENDPOINT],
  });
}

export const stockService = {
  getStockStats: (stocks: StockWithVariant[]) => {
    const safe = Array.isArray(stocks) ? stocks : [];
    const lowStockItems = safe.filter((s) => (s.stockInHand ?? 0) > 0 && (s.stockInHand ?? 0) < 10).length;
    const outOfStockItems = safe.filter((s) => (s.stockInHand ?? 0) === 0).length;
    return {
      totalVariants: safe.length,
      lowStockItems,
      outOfStockItems,
    };
  },
};
